// Concurrency-limited PUT uploader for R2 presigned URLs.
//
// Uses XMLHttpRequest because fetch() doesn't expose upload progress without
// streaming request bodies (spotty browser support). Per-file retry with
// exponential backoff. Caller passes presigned URLs from r2-presign-photos
// and gets back the keys that succeeded vs failed.

export interface PresignedFile {
  file: File;
  url: string;
  key: string;
  contentType: string;
  originalName: string;
}

export type UploadStatus = "pending" | "uploading" | "success" | "failed";

export interface UploadItem {
  key: string;
  originalName: string;
  size: number;
  status: UploadStatus;
  bytesUploaded: number;
  attempts: number;
  error?: string;
}

export interface UploadResult {
  uploadedKeys: string[];
  failedKeys: string[];
}

export interface UploadOptions {
  concurrency?: number;
  maxRetries?: number;
  signal?: AbortSignal;
  onUpdate?: (items: ReadonlyArray<UploadItem>) => void;
}

const DEFAULT_CONCURRENCY = 6;
const DEFAULT_MAX_RETRIES = 4;

const isRetryableStatus = (status: number) =>
  status === 0 || status === 408 || status === 429 || status >= 500;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

const backoffMs = (attempt: number) => {
  const base = 1000 * Math.pow(2, attempt);
  const jitter = Math.random() * 0.25 * base;
  return base + jitter;
};

interface PutOnceArgs {
  presigned: PresignedFile;
  onProgress: (bytesUploaded: number) => void;
  signal?: AbortSignal;
}

const putOnce = ({ presigned, onProgress, signal }: PutOnceArgs): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const onAbort = () => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };

    xhr.onload = () => {
      signal?.removeEventListener("abort", onAbort);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const err = new Error(`HTTP ${xhr.status}: ${xhr.statusText || "upload failed"}`);
        (err as Error & { status?: number }).status = xhr.status;
        reject(err);
      }
    };

    xhr.onerror = () => {
      signal?.removeEventListener("abort", onAbort);
      const err = new Error("Network error");
      (err as Error & { status?: number }).status = 0;
      reject(err);
    };

    xhr.onabort = () => {
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };

    xhr.open("PUT", presigned.url, true);
    xhr.setRequestHeader("Content-Type", presigned.contentType);
    xhr.send(presigned.file);
  });

export async function uploadFilesToR2(
  files: PresignedFile[],
  options: UploadOptions = {}
): Promise<UploadResult> {
  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;

  const items: UploadItem[] = files.map((f) => ({
    key: f.key,
    originalName: f.originalName,
    size: f.file.size,
    status: "pending",
    bytesUploaded: 0,
    attempts: 0,
  }));

  const emit = () => options.onUpdate?.(items);

  let cursor = 0;
  const claim = () => {
    while (cursor < items.length) {
      const idx = cursor++;
      if (items[idx].status === "pending") return idx;
    }
    return -1;
  };

  const uploadOne = async (idx: number) => {
    const item = items[idx];
    const presigned = files[idx];
    item.status = "uploading";
    item.bytesUploaded = 0;
    emit();

    while (true) {
      item.attempts += 1;
      try {
        await putOnce({
          presigned,
          signal: options.signal,
          onProgress: (bytes) => {
            item.bytesUploaded = bytes;
            emit();
          },
        });
        item.status = "success";
        item.bytesUploaded = item.size;
        item.error = undefined;
        emit();
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          item.status = "failed";
          item.error = "Aborted";
          emit();
          throw err;
        }
        const status = (err as Error & { status?: number }).status ?? 0;
        const message = err instanceof Error ? err.message : String(err);
        const canRetry = isRetryableStatus(status) && item.attempts <= maxRetries;
        if (!canRetry) {
          item.status = "failed";
          item.error = message;
          emit();
          return;
        }
        // Reset bytes for next attempt; backoff before retrying.
        item.bytesUploaded = 0;
        item.error = `${message} (retrying)`;
        emit();
        await sleep(backoffMs(item.attempts - 1), options.signal);
      }
    }
  };

  const worker = async () => {
    while (true) {
      const idx = claim();
      if (idx === -1) return;
      try {
        await uploadOne(idx);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        throw err;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => worker())
  );

  return {
    uploadedKeys: items.filter((i) => i.status === "success").map((i) => i.key),
    failedKeys: items.filter((i) => i.status === "failed").map((i) => i.key),
  };
}
